"use client";

import {useEffect, useRef, useState} from "react";
import type {ConversationSummary} from "@/lib/user/conversation-orchestration";
import {useI18n} from "@/lib/i18n/provider";

type Props = {
  title: string;
  conversations: ConversationSummary[];
  selectedThreadID: string | null;
  loading: boolean;
  error: boolean;
  onSelect: (threadID: string) => void;
  onRetry: () => void;
  onDelete: (threadID: string) => void;
};

/**
 * 最近会话下拉。触发器和菜单项都支持键盘，Esc 关闭后恢复触发器焦点；
 * 历史内容只展示服务端安全摘要，不在客户端拼接任务内部信息。
 */
export function ConversationHistoryMenu({title, conversations, selectedThreadID, loading, error, onSelect, onRetry, onDelete}: Props) {
  const {t} = useI18n();
  const [open, setOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<ConversationSummary | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const firstItemRef = useRef<HTMLButtonElement>(null);
  const cancelDeleteRef = useRef<HTMLButtonElement>(null);
  const close = (restoreFocus = true) => { setOpen(false); if (restoreFocus) window.setTimeout(() => triggerRef.current?.focus(), 0); };
  useEffect(() => { if (open) window.setTimeout(() => firstItemRef.current?.focus(), 0); }, [open]);
  useEffect(() => {
    if (!pendingDelete) return;
    window.setTimeout(() => cancelDeleteRef.current?.focus(), 0);
    // 确认框必须能用 Esc 安全退出，避免键盘用户被困在破坏性操作中。
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setPendingDelete(null);
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [pendingDelete]);

  return <div className="conversation-history-menu">
    <button
      ref={triggerRef}
      className="session-switch"
      type="button"
      aria-haspopup="menu"
      aria-expanded={open}
      onClick={() => setOpen((value) => !value)}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") { event.preventDefault(); setOpen(true); }
        if (event.key === "Escape" && open) { event.preventDefault(); close(); }
      }}
    >
      <span className="session-dot" />
      <span className="conversation-history-title">{title}</span>
      <span aria-hidden="true">⌄</span>
    </button>
    {open ? <div className="conversation-history-popover" role="menu" aria-label={t("chat.recentConversations")} onKeyDown={(event) => { if (event.key === "Escape") { event.preventDefault(); close(); } }}>
      <div className="conversation-history-heading">{t("chat.recentConversations")}</div>
      {loading ? <p role="status">{t("chat.historyLoading")}</p> : error ? <div className="conversation-history-state" role="alert"><span>{t("chat.historyError")}</span><button ref={firstItemRef} type="button" onClick={onRetry}>{t("common.retry")}</button></div> : conversations.length === 0 ? <p className="conversation-history-state">{t("chat.historyEmpty")}</p> : conversations.slice(0, 20).map((conversation, index) => <div key={conversation.id} className={`conversation-history-item ${conversation.id === selectedThreadID ? "is-current" : ""}`}>
        <button ref={index === 0 ? firstItemRef : undefined} className="conversation-history-select" type="button" role="menuitem" aria-current={conversation.id === selectedThreadID ? "true" : undefined} onClick={() => { onSelect(conversation.id); close(); }} onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); onSelect(conversation.id); close(); } }}><span>{conversation.title || t("chat.newConversation")}</span><small>{conversation.last_message_preview || t("chat.historyNoPreview")}</small></button>
        <button className="conversation-history-delete" type="button" role="menuitem" aria-label={t("chat.deleteConversation")} title={`${t("chat.deleteConversation")}：${conversation.title || t("chat.newConversation")}`} onClick={() => setPendingDelete(conversation)}><svg viewBox="0 0 20 20" aria-hidden="true"><path d="M4.5 6h11M8 3.5h4M6.5 6l.6 10h5.8l.6-10M8.5 8.5v5M11.5 8.5v5" /></svg></button>
      </div>)}
    </div> : null}
    {pendingDelete ? <div className="conversation-delete-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) setPendingDelete(null); }}><section className="conversation-delete-dialog" role="dialog" aria-modal="true" aria-labelledby="conversation-delete-title" aria-describedby="conversation-delete-description"><div className="conversation-delete-icon" aria-hidden="true"><svg viewBox="0 0 20 20"><path d="M4.5 6h11M8 3.5h4M6.5 6l.6 10h5.8l.6-10M8.5 8.5v5M11.5 8.5v5" /></svg></div><h2 id="conversation-delete-title">{t("chat.deleteConversation")}</h2><p id="conversation-delete-description">{t("chat.deleteConversationConfirm")}</p><div className="conversation-delete-actions"><button ref={cancelDeleteRef} type="button" className="conversation-delete-cancel" onClick={() => setPendingDelete(null)}>{t("common.cancel")}</button><button type="button" className="conversation-delete-confirm" onClick={() => { onDelete(pendingDelete.id); setPendingDelete(null); close(); }}>{t("chat.deleteConversation")}</button></div></section></div> : null}
  </div>;
}
